'use client';

import { useAuthStore } from '@/shared/stores/auth-store';
import type { Channel } from '@/types';

/**
 * - `unknown` — user not loaded yet (hydration render): render loading, never guess.
 * - `setting_up` — `/me` sent the key as null: not in the Creator Program yet.
 * - `active` — `/me` sent a FirstPromoter id.
 * - `unconfirmed` — the key is absent from `/me` altogether. The BE sends it to the owner
 *   (null when unset), so absence means the payload changed, not that the creator is out of the
 *   program: let the earnings route decide instead of hiding every creator's earnings.
 */
export type CreatorProgramStatus = 'unknown' | 'setting_up' | 'active' | 'unconfirmed';

/**
 * A channel joins the Creator Program when ops links it to a FirstPromoter promoter, which
 * `/me` exposes to the owner as `channel.firstpromoter_promoter_id`. Deciding from it up front
 * spares every earnings consumer a 403 `NO_PROMOTER_ACCOUNT` round-trip.
 */
export function getCreatorProgramStatus(channel: Channel | null | undefined): CreatorProgramStatus {
  if (!channel) return 'unknown';
  if (!('firstpromoter_promoter_id' in channel)) return 'unconfirmed';
  const id = channel.firstpromoter_promoter_id;
  if (id === null || id === undefined || String(id).trim() === '') return 'setting_up';
  return 'active';
}

/** Earnings may be requested: known active, or the payload can't tell and the server decides. */
export function canRequestEarnings(status: CreatorProgramStatus): boolean {
  return status === 'active' || status === 'unconfirmed';
}

export function useCreatorProgramStatus(): CreatorProgramStatus {
  const channel = useAuthStore((s) => s.user?.channel);
  return getCreatorProgramStatus(channel);
}
